import { Link } from "react-router-dom";
import type { EventItem } from "@/types";
import { formatDate, getCategoryName, statusLabels, statusStyles } from "@/utils/data";

export function CategoryTag({ categoryId }: { categoryId: string }) {
  return (
    <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
      {getCategoryName(categoryId)}
    </span>
  );
}

export function StatusTag({ status }: { status: string }) {
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyles[status]}`}>
      {statusLabels[status]}
    </span>
  );
}

export function EventRow({ event, showViews = true }: { event: EventItem; showViews?: boolean }) {
  return (
    <Link
      to={`/detail/${event.id}`}
      className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/40 sm:flex-row"
    >
      <div className="relative h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-44">
        <img
          src={event.cover}
          alt={event.title}
          title={`${event.title} 赛事资讯`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-primary-950/80 px-2 py-0.5 text-[11px] font-medium text-background-100">
          <i className="ri-calendar-line"></i>
          {formatDate(event.date)}
        </span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <CategoryTag categoryId={event.categoryId} />
          <StatusTag status={event.status} />
        </div>
        <h3 className="mt-2 line-clamp-2 text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
          {event.title}
        </h3>
        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">{event.summary}</p>
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-time-line"></i>
            {event.time}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-map-pin-line"></i>
            {event.location}
          </span>
          {showViews && (
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line"></i>
              {event.views.toLocaleString()} 阅读
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export function EventGridCard({ event }: { event: EventItem }) {
  return (
    <Link
      to={`/detail/${event.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden bg-background-200">
        <img
          src={event.cover}
          alt={event.title}
          title={`${event.title} 赛事资讯`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-3">
          <StatusTag status={event.status} />
          <span className="flex items-center gap-1 rounded-full bg-primary-950/80 px-2 py-0.5 text-[11px] font-medium text-background-100">
            <i className="ri-calendar-line"></i>
            {formatDate(event.date)}
          </span>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <CategoryTag categoryId={event.categoryId} />
        <h3 className="mt-2 line-clamp-2 text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
          {event.title}
        </h3>
        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">{event.summary}</p>
        <div className="mt-auto flex items-center justify-between pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line"></i>
            {event.views.toLocaleString()}
          </span>
          <span className="flex items-center gap-1 font-medium text-primary-700">
            查看详情
            <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </Link>
  );
}